import React from "react";
import { NumericFormat as NumberFormat } from "react-number-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * Cómo se muestra una oferta, en un solo lugar.
 *
 * <p>El descuento aparece en cinco pantallas —la card del listado, los carruseles, la ficha, el
 * carrito y el detalle de la venta— y en todas dice lo mismo: de cuánto venía, a cuánto queda y por
 * qué promoción. Tenerlo repetido en cinco JSX distintos garantizaba que tarde o temprano uno
 * redondeara distinto o se olvidara del tachado.
 *
 * <p>Son piezas sueltas y no un componente único de "precio" a propósito: cada pantalla tiene su
 * propio marcado de precio (la card lo parte en símbolo y número, el checkout lo alinea a la
 * derecha), así que lo que se comparte es el badge, el tachado y el cartel, no el layout.
 *
 * <p>El cálculo no está acá: el precio final lo resuelve el back (`OfertaPrecioResolver`) y viaja
 * dentro de `producto.oferta`. Acá sólo se pinta — si esto calculara algo, el listado y el checkout
 * podrían volver a mostrar números distintos.
 */

/**
 * Redondea a centavos.
 *
 * <p>Los importes llegan del back como BigDecimal exactos, pero acá se suman y se restan en
 * `Number`, que es binario: 1000 - 999.45 da 0.5500000000000007 y el checkout mostraba
 * "-$0,5500000000000007" en el renglón del descuento. Cualquier total que se calcule de este
 * lado tiene que pasar por acá antes de pintarse.
 */
export const redondearMonto = (valor) => Math.round((Number(valor) || 0) * 100) / 100;

/** El precio que se cobra hoy: el de la oferta si la hay, el de lista si no. */
export const precioVigente = (producto) => {
    if (!producto) return 0;
    return producto.oferta?.precioFinal ?? producto.precio;
};

/** Si la publicación tiene una oferta vigente que efectivamente le baja el precio. */
export const tieneOferta = (producto) => !!producto?.oferta?.precioFinal;

/** El total de una lista de publicaciones, ya con los descuentos aplicados. */
export const totalConOfertas = (productos) =>
    redondearMonto(
        (productos || []).reduce((total, prod) => total + Number(precioVigente(prod) || 0), 0)
    );

/** Lo que se ahorra en una lista de publicaciones. 0 si ninguna tiene oferta. */
export const ahorroTotal = (productos) =>
    redondearMonto(
        (productos || []).reduce(
            (total, prod) =>
                total + (tieneOferta(prod) ? Number(prod.precio || 0) - Number(prod.oferta.precioFinal || 0) : 0),
            0
        )
    );

/**
 * Importe con formato de pesos. El mismo en todas las piezas de acá.
 *
 * <p>Sin `decimalScale`: muestra los centavos sólo cuando el importe los tiene. Estaba en 0 y
 * eso alcanzaba mientras los precios eran enteros, pero el descuento sale de una cuenta —un 15%
 * sobre $6.663 da $999,45— y un ahorro de $0,55 se leía como "Ahorrás $1". El resto de las
 * pantallas (listado, ficha, checkout) ya venía formateando así.
 */
const Monto = ({ value }) => (
    <NumberFormat
        prefix={"$"}
        value={value ?? 0}
        displayType={"text"}
        thousandSeparator="."
        decimalSeparator=","
    />
);

/**
 * La pastilla del porcentaje: "-15%".
 *
 * <p>Es lo único que se ve desde lejos en una grilla de cards, así que va sobre la imagen y no junto
 * al precio: quien escanea el listado decide ahí si frena o sigue.
 */
export const BadgeOferta = ({ oferta, className = "" }) => {
    if (!oferta || !oferta.descuentoPct) return null;

    return (
        <span className={`oferta__badge ${className}`.trim()} title={oferta.nombre}>
            -{oferta.descuentoPct}%
        </span>
    );
};

/** El precio anterior, tachado. Va arriba o al lado del precio final, según la pantalla. */
export const PrecioTachado = ({ oferta, className = "" }) => {
    if (!oferta) return null;

    return (
        <s className={`oferta__lista ${className}`.trim()}>
            <Monto value={oferta.precioLista} />
        </s>
    );
};

/**
 * El cartel que dice de qué promoción es el descuento, hasta cuándo corre y con qué condiciones.
 *
 * <p>Va en la ficha y en el carrito, no en la card: en la grilla no hay lugar y el badge ya dijo lo
 * único que importa a esa altura. Acá, en cambio, la persona ya está decidiendo, y "termina el 30 de
 * septiembre" o "no incluye envío" es exactamente lo que necesita saber antes de pagar.
 */
export const CartelOferta = ({ oferta, compacto = false }) => {
    if (!oferta) return null;

    const ahorro = redondearMonto(Number(oferta.precioLista || 0) - Number(oferta.precioFinal || 0));

    return (
        <div className={compacto ? "oferta__cartel oferta__cartel--compacto" : "oferta__cartel"}>
            <span className="oferta__cartel-titulo">
                <FontAwesomeIcon icon="fa-solid fa-tag" />
                {oferta.nombre}
            </span>

            <span className="oferta__cartel-ahorro">
                Ahorrás <Monto value={ahorro} /> ({oferta.descuentoPct}%)
            </span>

            {oferta.descripcion && <span className="oferta__cartel-texto">{oferta.descripcion}</span>}

            {oferta.hasta && (
                <span className="oferta__cartel-vigencia">
                    <FontAwesomeIcon icon="fa-regular fa-clock" />
                    Válida hasta el {formatearFecha(oferta.hasta)}
                </span>
            )}

            {oferta.condiciones && <small className="oferta__cartel-condiciones">{oferta.condiciones}</small>}
        </div>
    );
};

/**
 * "2026-09-30" → "30/09/2026".
 *
 * <p>Se parte el string en vez de construir un `Date`: `new Date("2026-09-30")` se interpreta como
 * medianoche UTC y en Argentina (UTC-3) muestra el día anterior, así que una oferta que vence el 30
 * decía "válida hasta el 29".
 */
const formatearFecha = (fecha) => {
    if (!fecha) return "";
    const [anio, mes, dia] = String(fecha).split("-");
    return dia ? `${dia}/${mes}/${anio}` : fecha;
};
